import axios, { AxiosResponse } from 'axios';
import { mutate } from 'swr';
import { SERVICES } from '@shared/constants/apiConfig';
import { withQueryParams } from '@shared/commonFunctions';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { getError } from '@shared/utils/helpers';
import { IPullPayOffQuoteResponse } from '../../interfaces';
import { IAssetOnReportsResponse } from '../Deals/components/Tiles/interfaces';
import { IAsset, INcicCodeResponse, IPartnerResponse, IPullPayOffQuoteRequest, IVehicleFeature, IPlateToVin, IPlateToVinResp, IBumperPlateResponse, IBumperPlateToVin } from './interfaces';

const path = `/${SERVICES.ASSET}/assets`;
const contactServicePath = `/${SERVICES.CONTACT}/contacts`;

export const requestCreateAsset = (data: IAsset) => {
  return mutate(path, async () => {
    try {
      const response = await axios.post(path, data);
      Notistack.successMessage(' Asset - Created Successfully');
      return response.data;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestUpdateAsset = (data: IAsset, hideNotification = false) => {
  return mutate(path, async () => {
    try {
      const response : AxiosResponse<IAsset> = await axios.put(`${path}/${data.id}`, data);
      !hideNotification && Notistack.successMessage('Asset - Updated Successfully');
      return response.data;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestPullVehicleData = (vin: string) => {
  const url = `${path}/${vin}/summary`;

  return mutate(url, async() => {
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (err) {
      Notistack.generalError(`Cannot Decode VIN ${vin}`);
    }
  });
};





export const requestBookoutPdf = (
  data: IVehicleFeature,
  assetId: string,
  vinNumber: string,
  assetValuation: string | null,
) => {
  // const url = `${path}/${assetId}/vehicle/bookout/download?vinNumber=${vinNumber}&assetValuationType=${assetValuation}`;
  const url = withQueryParams(
    `${path}/${assetId}/bookout/download`,
    { vinNumber, assetValuationType: assetValuation },
    false,
  );

  return mutate(path, async() => {
    try {
      const response = await axios.post(url, data);
      Notistack.successMessage('Asset - '+ assetValuation?.toUpperCase() +' Bookout sheet generated successfully');
      return response.data;
    } catch (err) {
		Notistack.generalError(getError(err));
    }
  });
};

export const requestContactDetails = (id: string) => {
  const url = `${contactServicePath}/` + id;

  return mutate(url, async() => {
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (err) {
		Notistack.generalError(getError(err));
    }
  });
};

export const requestPullPayOffInfo = (partnerCode: string): Promise<IPartnerResponse | undefined> => {
const url = `${SERVICES.ASSET}/v2/lienholder/partners/${partnerCode}`;
  return mutate<IPartnerResponse>(url, async() => {
    try {
      const respsone = await axios.get(url);
      return respsone.data;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestMSRPData = (assetId: string, vin: string) => {
  const url = `${SERVICES.ASSET}/v2/assets/${assetId}/sticker/${vin}?assetStickerType=Monroney`;

  return mutate(url, async() => {
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestAcceleratedTitleSupport = (partnerCode : string | undefined) => {
	const url = `/${SERVICES.ASSET}/v2/lienholder/partners/${partnerCode}`;

	return mutate(url, async() => {
		try {
			const response = await axios.get(url);
			return response.data;
		} catch (err) {
			Notistack.generalError(getError(err));
		}
	});
};


export const requestPullPayOffQuote = (
  partnerCode : string,
  postData: IPullPayOffQuoteRequest,
): Promise<IPullPayOffQuoteResponse | undefined> => {
  const url = `${SERVICES.ASSET}/v2/lienholder/pull-payOff-quote/${partnerCode}`;
  return mutate(url, async() => {
    try {
      const response = await axios.post(url, postData);
      return response.data;
    } catch (err) {
		Notistack.generalError(getError(err));
    }
  });
};


export const requestNcicCode = (
): Promise<INcicCodeResponse[] | []> => {
  const path = `/${SERVICES.ASSET}/nciccode`;
  return mutate(path, async() => {
    try {
     
      return (await axios.get(`${path}`)).data;
    } catch (err) {
		Notistack.generalError(getError(err));
    }
  });
};

export const requestNcicCodeByMake = (
  make : string
): Promise<INcicCodeResponse | undefined> => {
  const path = `/${SERVICES.ASSET}/nciccode`;
  return mutate(path, async() => {
    try {
      if (make) {
           return (await axios.get(`${path}/${make}`)).data;
      }
      return (await axios.get(`${path}`)).data;
    } catch (err) {
		Notistack.generalError(getError(err));
    }
  });
};

export const requestPlateToVin = async (payload: IPlateToVin) => {
  const path = `${SERVICES.ASSET}/vingateway/plate-to-vin`;

  try {
    const resp = await axios.post<IPlateToVinResp>(path, payload);
    return resp.data;
  } catch (err) {
    Notistack.generalError(getError(err));
  }
};

export const requestBumperPlateToVin = async (payload: IBumperPlateToVin) => {
  if (!payload || !payload.plateNumber || !payload.plateState) {
    return;
  }
  const url = `${SERVICES.EXTERNAL}/bumper/plate-to-vin/${payload.plateNumber}/${payload.plateState}`;
  try {
    const resp = await axios.get<IBumperPlateResponse>(url);
    return resp.data;
  } catch (err) {
    Notistack.generalError(getError(err));
  }
};

export const getAssetOnReport = async(assetId: string) => {
  const path = `${SERVICES.ASSET}/assets/${assetId}/lien-on-credit-reports`;

  try {
    const resp = await axios.get<IAssetOnReportsResponse>(path);
    return resp.data;
  } catch (err) {
    Notistack.generalError(getError(err));
  }
};